import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card, Btn, Select, Empty } from "@/components/UI";
import { useLang } from "@/lib/i18n";
import { api, fmtIDR, formatApiError } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Enrollment() {
  const { t } = useLang();
  const [step, setStep] = useState(1);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]);

  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data || []));
    api.get("/classes").then((r) => setClasses(r.data || []));
  }, []);

  const totalFee = selectedClasses.reduce((sum, cid) => {
    const c = classes.find((x) => x.id === cid);
    return sum + (c?.fee_amount || 0);
  }, 0);

  const submit = async () => {
    try {
      for (const cid of selectedClasses) {
        await api.post("/enrollments", { student_id: studentId, class_id: cid });
      }
      toast.success(t.toastEnrollmentConfirmed);
      setStep(1);
      setStudentId("");
      setSelectedClasses([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const steps = [t.studentInfo, t.selectCourses, t.feePlan, t.confirmStep];

  return (
    <Layout title={t.enrollment} subtitle={t.subWizard}>
      {/* Stepper */}
      <Card className="mb-5" testid="enrollment-stepper">
        <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center sm:justify-between">
          {steps.map((label, i) => (
            <div key={label} className="flex items-center sm:flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <div
                  className={`step-dot ${step >= i + 1 ? "is-on" : ""} ${step === i + 1 ? "is-now" : ""}`}
                  aria-current={step === i + 1 ? "step" : undefined}
                >
                  {step > i + 1 ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`min-w-0 text-xs font-bold font-heading ${step >= i + 1 ? "text-slate-900" : "text-slate-600"}`}>{label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`step-line hidden flex-1 mx-3 sm:block ${step > i + 1 ? "is-on" : ""}`} />
              )}
            </div>
          ))}
        </div>
      </Card>

      {step === 1 && (
        <Card title={t.studentInfo}>
          <Select
            label={t.selectStudent}
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            testid="enroll-select-student"
            options={[{ value: "", label: "—" }, ...students.map((s) => ({ value: s.id, label: `${s.full_name} (${s.student_code})` }))]}
          />
          <div className="mt-5 flex justify-end">
            <Btn disabled={!studentId} onClick={() => setStep(2)} testid="enroll-next-1">{t.next}</Btn>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card title={t.selectCourses} testid="enroll-courses">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {classes.map((c) => {
              const checked = selectedClasses.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`enroll-class-${c.id}`}
                  onClick={() =>
                    setSelectedClasses(checked ? selectedClasses.filter((x) => x !== c.id) : [...selectedClasses, c.id])
                  }
                  aria-pressed={checked}
                  className={`pick-card ${checked ? "is-on" : ""}`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-sm">{c.subject_name}</div>
                      <div className="text-xs text-slate-600 font-sans mt-0.5">{c.educator_name} · {c.room}</div>
                      <div className="text-xs text-slate-600 font-sans mt-1">
                        {(c.schedule_days || []).map((d) => t.days[d]).join(", ")} · {c.time_start}–{c.time_end}
                      </div>
                    </div>
                    <div className="text-sm font-heading font-bold sm:text-right">{fmtIDR(c.fee_amount)}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-5 flex flex-wrap justify-between gap-2">
            <Btn variant="ghost" onClick={() => setStep(1)}>{t.back}</Btn>
            <Btn disabled={selectedClasses.length === 0} onClick={() => setStep(3)} testid="enroll-next-2">{t.next}</Btn>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card title={t.feePlan} testid="enroll-feeplan">
          <div className="space-y-2">
            {selectedClasses.map((cid) => {
              const c = classes.find((x) => x.id === cid);
              return (
                <div key={cid} className="line-row">
                  <span className="text-sm font-sans">{c?.subject_name}</span>
                  <span className="text-sm font-heading font-semibold">{fmtIDR(c?.fee_amount)}</span>
                </div>
              );
            })}
            <div className="clay-well flex flex-wrap justify-between gap-2 mt-3">
              <span className="font-heading font-semibold">{t.totalPerMonth}</span>
              <span className="font-heading font-bold text-lg">{fmtIDR(totalFee)}</span>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap justify-between gap-2">
            <Btn variant="ghost" onClick={() => setStep(2)}>{t.back}</Btn>
            <Btn onClick={() => setStep(4)} testid="enroll-next-3">{t.next}</Btn>
          </div>
        </Card>
      )}

      {step === 4 && (
        <Card title={t.confirmStep} testid="enroll-confirm">
          <div className="space-y-2 text-sm">
            <div className="flex flex-wrap justify-between gap-2"><span className="text-slate-500">{t.student}</span><span className="font-heading font-semibold">{students.find((s) => s.id === studentId)?.full_name}</span></div>
            <div className="flex flex-wrap justify-between gap-2"><span className="text-slate-500">{t.classes}</span><span className="font-heading font-semibold text-right">{selectedClasses.map((cid) => classes.find((c) => c.id === cid)?.subject_name).join(", ")}</span></div>
            <div className="flex flex-wrap justify-between gap-2"><span className="text-slate-500">{t.totalPerMonth}</span><span className="font-heading font-bold">{fmtIDR(totalFee)}</span></div>
          </div>
          <div className="mt-5 flex flex-wrap justify-between gap-2">
            <Btn variant="ghost" onClick={() => setStep(3)}>{t.back}</Btn>
            <Btn variant="success" onClick={submit} testid="enroll-submit">{t.confirm}</Btn>
          </div>
        </Card>
      )}
    </Layout>
  );
}
